import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './ui/select';
import { LogOut, AlertTriangle, Users, CheckSquare, Briefcase, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * Exit Employee Wizard
 * Walks a Super Admin through reassigning everything an exiting user owns,
 * then deactivates / deletes them in one shot.
 */
export const ExitEmployeeDialog = ({ open, onOpenChange, user, allUsers = [], onExited }) => {
  const [step, setStep] = useState(1);
  const [impact, setImpact] = useState(null);
  const [memberAssignments, setMemberAssignments] = useState({});
  const [taskRecipientId, setTaskRecipientId] = useState('');
  const [clientRecipientId, setClientRecipientId] = useState('');
  const [deactivate, setDeactivate] = useState(true);
  const [typedName, setTypedName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Admins eligible to receive things (active, not the exiting user)
  const activeAdmins = allUsers.filter(u => u.role === 'admin' && u.is_active !== false && u.id !== user?.id);
  const activeTaskRecipients = allUsers.filter(u => u.is_active !== false && u.id !== user?.id && u.role !== 'super_admin');

  useEffect(() => {
    if (!open || !user) return;
    setStep(1); setMemberAssignments({}); setTaskRecipientId(''); setClientRecipientId('');
    setDeactivate(true); setTypedName(''); setImpact(null);
    setLoading(true);
    api.get(`/users/${user.id}/exit-impact`)
      .then(res => {
        setImpact(res.data);
        // Default each direct report to the first available admin
        const defaults = {};
        const firstAdmin = (allUsers.find(u2 => u2.role === 'admin' && u2.is_active !== false && u2.id !== user.id) || {}).id || '';
        (res.data.direct_reports || []).forEach(m => { defaults[m.id] = firstAdmin; });
        setMemberAssignments(defaults);
        if (firstAdmin) {
          setTaskRecipientId(firstAdmin);
          setClientRecipientId(firstAdmin);
        }
      })
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load exit impact'))
      .finally(() => setLoading(false));
  }, [open, user, allUsers]);

  if (!user) return null;

  const totalImpact = impact
    ? (impact.direct_reports?.length || 0) +
      (impact.open_assignee_tasks || 0) +
      (impact.open_reviewer_tasks || 0) +
      (impact.owned_clients || 0) +
      (impact.service_mapped_clients || 0)
    : 0;

  const needsTaskRecipient = impact && ((impact.open_assignee_tasks || 0) + (impact.open_reviewer_tasks || 0)) > 0;
  const needsClientRecipient = impact && ((impact.owned_clients || 0) + (impact.service_mapped_clients || 0)) > 0;

  const canSubmit =
    impact &&
    (impact.direct_reports || []).every(m => !!memberAssignments[m.id]) &&
    (!needsTaskRecipient || !!taskRecipientId) &&
    (!needsClientRecipient || !!clientRecipientId) &&
    typedName.trim() === (user.full_name || '').trim();

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/users/${user.id}/exit`, {
        member_assignments: memberAssignments,
        task_recipient_id: taskRecipientId || null,
        client_recipient_id: clientRecipientId || null,
        deactivate,
      });
      const s = res.data?.summary || {};
      toast.success(
        `${user.full_name} exited. Moved ${s.members_moved || 0} member(s), ${s.tasks_assignee_moved || 0 + (s.tasks_reviewer_moved || 0)} task(s), ${s.clients_moved || 0} client(s).`
      );
      onOpenChange(false);
      if (onExited) onExited();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to exit user');
    } finally {
      setSubmitting(false);
    }
  };

  const SummaryRow = ({ icon: Icon, label, value, color }) => (
    <div className="flex items-center justify-between px-3 py-2 rounded-md bg-slate-50">
      <div className="flex items-center gap-2 text-slate-700">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-sm">{label}</span>
      </div>
      <Badge className="bg-slate-900 text-white">{value}</Badge>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="exit-employee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <LogOut className="h-5 w-5" />
            Exit Employee: {user.full_name}
          </DialogTitle>
          <DialogDescription>
            Reassign this user's work, then deactivate or delete the account in one operation.
          </DialogDescription>
        </DialogHeader>

        {loading || !impact ? (
          <div className="py-10 text-center text-slate-400 text-sm">Calculating impact…</div>
        ) : (
          <>
            {/* Stepper */}
            <div className="flex items-center gap-2 mb-2">
              {[1, 2, 3].map(s => (
                <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? 'bg-red-500' : 'bg-slate-200'}`} />
              ))}
            </div>
            <p className="text-xs text-slate-400 mb-3">Step {step} of 3</p>

            {step === 1 && (
              <div className="space-y-3" data-testid="exit-step-1">
                <p className="text-sm text-slate-600">
                  {totalImpact === 0
                    ? 'This user has nothing assigned. You can proceed straight to deactivation.'
                    : 'Here is what currently belongs to this user. Each item will be reassigned in the next steps.'}
                </p>
                <SummaryRow icon={Users} label="Direct reports (members)" value={impact.direct_reports?.length || 0} color="text-indigo-500" />
                <SummaryRow icon={CheckSquare} label="Open tasks where they are assignee" value={impact.open_assignee_tasks || 0} color="text-blue-500" />
                <SummaryRow icon={CheckSquare} label="Open tasks where they are reviewer" value={impact.open_reviewer_tasks || 0} color="text-cyan-500" />
                <SummaryRow icon={Briefcase} label="Clients owned (assigned_admin)" value={impact.owned_clients || 0} color="text-amber-500" />
                <SummaryRow icon={Briefcase} label="Clients with service-map entries" value={impact.service_mapped_clients || 0} color="text-amber-500" />
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="exit-step-2">
                {(impact.direct_reports?.length || 0) > 0 && (
                  <div>
                    <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
                      <Users className="h-4 w-4 text-indigo-500" /> Move each direct report to:
                    </Label>
                    <div className="space-y-2">
                      {impact.direct_reports.map(m => (
                        <div key={m.id} className="flex items-center gap-3" data-testid={`exit-member-${m.id}`}>
                          <span className="text-sm w-40 truncate text-slate-700">{m.full_name}</span>
                          <Select value={memberAssignments[m.id] || ''} onValueChange={(v) => setMemberAssignments(prev => ({ ...prev, [m.id]: v }))}>
                            <SelectTrigger className="flex-1 h-9" data-testid={`exit-member-target-${m.id}`}>
                              <SelectValue placeholder="Select new admin" />
                            </SelectTrigger>
                            <SelectContent>
                              {activeAdmins.map(a => <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {needsTaskRecipient && (
                  <div>
                    <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
                      <CheckSquare className="h-4 w-4 text-blue-500" />
                      Transfer remaining open tasks (assignee + reviewer) to:
                    </Label>
                    <Select value={taskRecipientId} onValueChange={setTaskRecipientId}>
                      <SelectTrigger className="h-9" data-testid="exit-task-recipient">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeTaskRecipients.map(a => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.full_name} <span className="text-slate-400 text-xs ml-1">({a.role})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {needsClientRecipient && (
                  <div>
                    <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
                      <Briefcase className="h-4 w-4 text-amber-500" />
                      Transfer clients (direct + service-map) to admin:
                    </Label>
                    <Select value={clientRecipientId} onValueChange={setClientRecipientId}>
                      <SelectTrigger className="h-9" data-testid="exit-client-recipient">
                        <SelectValue placeholder="Select admin" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeAdmins.map(a => <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {totalImpact === 0 && (
                  <p className="text-sm text-slate-500 italic">Nothing to reassign for this user.</p>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="exit-step-3">
                <div className="p-3 rounded-md border border-red-200 bg-red-50">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      Final step. After confirmation this user will no longer be able to log in.
                      All reassignments will be applied atomically.
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold mb-2 block">Final state for {user.full_name}:</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setDeactivate(true)}
                      data-testid="exit-mode-deactivate"
                      className={`p-3 rounded-md border-2 text-left ${deactivate ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <p className="font-semibold text-sm">Deactivate</p>
                      <p className="text-xs text-slate-500 mt-0.5">Keeps history, blocks login. Recommended.</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeactivate(false)}
                      data-testid="exit-mode-delete"
                      className={`p-3 rounded-md border-2 text-left ${!deactivate ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <p className="font-semibold text-sm">Permanently delete</p>
                      <p className="text-xs text-slate-500 mt-0.5">Removes the user record entirely.</p>
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold mb-1.5 block">
                    Type <span className="text-red-600 font-mono">{user.full_name}</span> to confirm:
                  </Label>
                  <Input
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder={user.full_name}
                    data-testid="exit-confirm-name"
                  />
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter className="flex items-center justify-between gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} disabled={submitting} data-testid="exit-back">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            {step < 3 && (
              <Button onClick={() => setStep(step + 1)} disabled={loading || !impact} data-testid="exit-next"
                className="bg-slate-900 hover:bg-slate-800">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 3 && (
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="exit-submit"
              >
                {submitting ? 'Exiting…' : `Exit ${user.full_name}`}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
